/**
 * PlanAheadOverviewTab — the Overview surface for /plan-ahead.
 *
 * Phase 85.4. This file used to ship placeholders: ₹28,500 of goal
 * contributions, a ₹48,500 "Building Money" total and three "Needs Attention"
 * lines, all hardcoded, all rendered identically to every user. A real user
 * with zero accounts, zero transactions, zero goals and zero uploads was shown
 * ₹48,500 of his own savings on 2026-08-26. Nothing leaked — it was invented,
 * which for a product whose only asset is being believable is worse.
 *
 * Every figure here now comes from `GET /api/planning/summary`, which returns
 * only what the database can support and marks what it cannot. Where there is
 * nothing to show, this component says so. It never renders a number the user
 * did not put there.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { ArrowRight, ChevronDown, Plus, AlertTriangle } from 'lucide-react';
import { formatMoney } from '@/lib/format';

const STATUS_META = {
  on_track:         { label: 'On Track',        color: '#3D7A63', bg: '#EEF2EF' },
  active:           { label: 'On Track',        color: '#3D7A63', bg: '#EEF2EF' },
  building:         { label: 'Building',        color: '#3E6A80', bg: '#EAF1F5' },
  needs_attention:  { label: 'Needs Attention', color: '#B07210', bg: '#FBF5E8' },
  paused:           { label: 'Paused',          color: '#9B9890', bg: '#F2F1EE' },
  completed:        { label: 'Completed',       color: '#163A32', bg: '#DFEEE5' },
};

function ProgressBar({ pct, size = 'md', color = '#163A32' }) {
  const h = size === 'lg' ? 'h-2.5' : size === 'sm' ? 'h-1' : 'h-1.5';
  return (
    <div className={`${h} bg-border/40 rounded-full overflow-hidden`}>
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color }} />
    </div>
  );
}

function Section({ children, className = '' }) {
  return (
    <div className={`bg-card rounded-xl border border-border p-5 lg:p-6 shadow-sm ${className}`}>{children}</div>
  );
}

export default function PlanAheadOverviewTab({ onGoToGoals, onGoToProjections, baseCurrency = 'INR' }) {
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const [actionDismissed, setActionDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('goals').select('*').order('created_at', { ascending: true });
      if (!cancelled) setGoals(data || []);
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess?.session?.access_token;
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/planning/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) setSummary(await res.json());
      } catch {
        /* An unreachable endpoint shows nothing, never a placeholder. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const active = useMemo(() => goals.filter((g) => (g.status || 'active') !== 'completed'), [goals]);
  const priority = active[0] || null;
  const secondary = active.slice(1, 4);

  const currency = summary?.base_currency || baseCurrency;
  const building = summary?.building_money || null;
  const buildingAvailable = Boolean(building?.available);
  const buildingTotal = buildingAvailable ? (building.this_month || 0) : null;
  const trackedComponents = (building?.components || []).filter((c) => c.tracked && c.amount != null);
  const untracked = (building?.components || []).filter((c) => !c.tracked);
  const maxB = Math.max(1, ...trackedComponents.map((c) => c.amount || 0));
  const needsAttention = summary?.needs_attention || [];

  const priorityPct = priority ? Math.round(((priority.current_amount || 0) / Math.max(1, priority.target_amount || 1)) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="plan-ahead-overview">
      {/* 1. Planning Briefing */}
      <Section className="border-l-[3px] border-l-[#163A32]">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-4">Your Planning Briefing</p>
        <p className="font-fraunces text-xl font-medium text-charcoal leading-snug mb-2">
          {active.length > 0
            ? `You're making progress on ${active.length} active goal${active.length === 1 ? '' : 's'}.`
            : "You haven't set any active goals yet."}
        </p>
        {buildingAvailable ? (
          <p className="text-sm text-charcoal/70 leading-relaxed mb-1.5" data-testid="plan-briefing-contributed">
            You logged <strong className="text-charcoal font-medium">{formatMoney(buildingTotal, currency)}</strong> toward your goals this month.
          </p>
        ) : (
          <p className="text-sm text-charcoal/70 leading-relaxed mb-1.5" data-testid="plan-briefing-no-contributions">
            {active.length > 0
              ? 'No contributions logged this month yet. Log one against a goal and it will appear here.'
              : 'Once you add an account or create a goal, your planning figures appear here — built only from your own numbers.'}
          </p>
        )}
        {buildingAvailable && building.last_month != null && (
          <p className="text-xs text-muted-foreground/70 mb-4" data-testid="plan-briefing-last-month">
            Last month: {formatMoney(building.last_month, currency)}.
          </p>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={onGoToGoals} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-evergreen hover:opacity-90 transition-opacity" data-testid="plan-briefing-primary">
            Review your priorities
          </button>
          <button onClick={() => setWhyOpen(!whyOpen)} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-charcoal transition-colors">
            Why this matters <ChevronDown className={`h-3 w-3 transition-transform ${whyOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {whyOpen && (
          <div className="mt-4 pt-4 border-t border-border/60 text-sm text-charcoal/70 leading-relaxed space-y-2">
            <p>Your priority goal is your first anchor. Completing it before pursuing others is a sound sequencing decision.</p>
            <p>Goals without regular contribution slowly drift later. A resumed monthly amount, even small, restores the projected completion date.</p>
          </div>
        )}
      </Section>

      {/* 2. What You're Building */}
      <Section>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-xs font-semibold text-charcoal mb-0.5">What You&apos;re Building</p>
            <p className="text-xs text-muted-foreground">Your active financial goals and the progress you&apos;re making toward them.</p>
          </div>
          <button onClick={onGoToGoals} className="text-xs font-medium flex items-center gap-1 shrink-0 hover:opacity-70 transition-opacity text-evergreen" data-testid="plan-goals-view-all">
            View all <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        {priority ? (
          <>
            <div className="mb-5 pb-5 border-b border-border/60">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-1">Priority</p>
                  <p className="font-fraunces text-lg font-medium text-charcoal">{priority.name}</p>
                  {priority.description && <p className="text-xs text-muted-foreground mt-0.5">{priority.description}</p>}
                </div>
                {(() => {
                  const sm = STATUS_META[priority.status] || STATUS_META.active;
                  return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: sm.bg, color: sm.color }}>{sm.label}</span>;
                })()}
              </div>
              <div className="flex items-end gap-2 mb-2">
                <p className="num font-fraunces text-2xl font-medium text-charcoal">{formatMoney(priority.current_amount || 0, priority.currency || baseCurrency)}</p>
                <p className="text-muted-foreground text-sm mb-0.5">of {formatMoney(priority.target_amount || 0, priority.currency || baseCurrency)}</p>
                <p className="text-sm text-muted-foreground/70 mb-0.5 ml-auto num">{priorityPct}% funded</p>
              </div>
              <ProgressBar pct={priorityPct} size="md" />
            </div>

            {secondary.length > 0 && (
              <div className="space-y-4">
                {secondary.map((g) => {
                  const p = Math.round(((g.current_amount || 0) / Math.max(1, g.target_amount || 1)) * 100);
                  const sm = STATUS_META[g.status] || STATUS_META.active;
                  return (
                    <div key={g.id}>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <p className="text-sm font-medium text-charcoal truncate">{g.name}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: sm.bg, color: sm.color }}>{sm.label}</span>
                          <p className="text-xs text-muted-foreground/80 num">{p}%</p>
                        </div>
                      </div>
                      <ProgressBar pct={p} size="sm" color={sm.color} />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No goals yet. Head to the Goals tab to create your first one.
          </p>
        )}

        <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border/60">
          <button onClick={onGoToGoals} className="text-sm font-medium px-4 py-2 rounded-lg border border-evergreen/30 text-evergreen hover:bg-evergreen/5 transition-colors">View all goals</button>
          <button onClick={onGoToGoals} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-charcoal transition-colors">
            <Plus className="h-3 w-3" /> Create goal
          </button>
        </div>
      </Section>

      {/* 3. Next Best Planning Action */}
      {!actionDismissed && priority && (
        <Section>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-4">Next Best Planning Action</p>
          <p className="font-fraunces text-lg font-medium text-charcoal mb-2">Restart a monthly contribution to keep your goals on pace.</p>
          <p className="text-sm text-charcoal/70 leading-relaxed mb-4">Even a small, regular amount keeps the estimated completion from drifting later.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={onGoToGoals} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-evergreen hover:opacity-90 transition-opacity">Review goal plan</button>
            <button onClick={() => setActionDismissed(true)} className="ml-auto text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors">Not now</button>
          </div>
        </Section>
      )}

      {/* 4. Looking Ahead */}
      <Section>
        <p className="text-xs font-semibold text-charcoal mb-1">Looking Ahead</p>
        <p className="text-xs text-muted-foreground mb-5">Useful actions across different time horizons.</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[
            { horizon: 'This Week',   action: 'Review your priority goal plan.',           context: 'Small check-ins keep momentum steady.',       color: '#A84E38', bg: '#F5ECEA' },
            { horizon: 'This Month',  action: 'Review recurring commitments.',              context: 'Trim what no longer serves your goals.',       color: '#B07210', bg: '#FBF5E8' },
            { horizon: 'Longer Term', action: 'Complete your foundation goal.',            context: 'A finished foundation frees energy for the next.', color: '#163A32', bg: '#EEF2EF' },
          ].map((item) => (
            <div key={item.horizon} className="rounded-xl p-4" style={{ backgroundColor: item.bg }}>
              <p className="text-[9px] uppercase tracking-widest font-bold mb-2" style={{ color: item.color }}>{item.horizon}</p>
              <p className="text-sm font-medium text-charcoal mb-1.5">{item.action}</p>
              <p className="text-xs text-charcoal/60 leading-relaxed mb-3">{item.context}</p>
              <button onClick={onGoToGoals} className="text-xs font-medium flex items-center gap-1 transition-opacity hover:opacity-70" style={{ color: item.color }} data-testid={`plan-horizon-action-${item.horizon.replace(/\s+/g, '-').toLowerCase()}`}>
                Take action <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Building Money — user-logged contributions only, or nothing at all */}
      <Section>
        <p className="text-xs font-semibold text-charcoal mb-0.5">Building Money</p>
        <p className="text-xs text-muted-foreground mb-5">Money you have logged toward your goals. Only contributions you recorded yourself.</p>
        {buildingAvailable ? (
          <>
            <div className="flex items-end gap-3 mb-4">
              <p className="font-fraunces text-3xl font-medium text-charcoal num" data-testid="plan-building-total">{formatMoney(buildingTotal, currency)}</p>
              <p className="text-sm text-muted-foreground mb-1">this month</p>
            </div>
            <div className="space-y-3">
              {trackedComponents.map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <p className="text-sm text-charcoal/80 w-52 shrink-0">{item.label}</p>
                  <div className="flex-1 h-1.5 bg-border/40 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${((item.amount || 0) / maxB) * 100}%`, backgroundColor: '#163A32' }} />
                  </div>
                  <p className="num text-sm font-semibold text-charcoal w-24 text-right shrink-0">{formatMoney(item.amount, currency)}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground py-6" data-testid="plan-building-empty">
            Nothing logged yet. When you record a contribution against a goal, the total appears here — and nowhere before that.
          </p>
        )}
        {untracked.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border/60 space-y-1.5" data-testid="plan-building-not-tracked">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">Not counted yet</p>
            {untracked.map((c) => (
              <p key={c.label} className="text-xs text-muted-foreground leading-relaxed">
                <span className="text-charcoal/80">{c.label}</span> — {c.why}
              </p>
            ))}
          </div>
        )}
      </Section>

      {/* 6. Needs Attention — derived from real evidence; hidden when there is none */}
      {needsAttention.length > 0 && (
      <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm" data-testid="plan-needs-attention">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border/60 bg-offwhite/40">
          <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#B07210' }} />
          <div>
            <p className="text-xs font-semibold text-charcoal">Needs Attention</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Planning areas worth reviewing.</p>
          </div>
        </div>
        <div className="divide-y divide-border/40">
          {needsAttention.map((item, i) => (
            <div key={item.code + i} className="px-5 py-4" data-testid={`plan-needs-attention-item-${i}`}>
              <p className="text-sm font-medium text-charcoal mb-1">{item.headline}</p>
              {item.context ? (
                <p className="text-xs text-muted-foreground mb-2 leading-relaxed">{item.context}</p>
              ) : null}
              <button
                onClick={item.code === 'goal_contributions_paused' ? onGoToGoals : undefined}
                className="text-xs font-medium flex items-center gap-1 hover:opacity-70 transition-opacity"
                style={{ color: '#B07210' }}
                data-testid={`plan-needs-attention-action-${i}`}
              >
                {item.code === 'goal_contributions_paused' ? 'Review contribution plan' : 'Update this balance'} <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      <button onClick={onGoToProjections} className="text-sm text-evergreen hover:opacity-70 transition-opacity flex items-center gap-1 mx-auto" data-testid="plan-view-projections-cta">
        View projections <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
